import { Expose, Transform, Type } from 'class-transformer';
import { IsBoolean, IsEnum, IsNumber, IsObject, IsOptional, IsString, ValidateNested } from 'class-validator';
import Address from './address.entity';
import CondominiumType from './condominium-type.entity';
import CreatedInfo, { Created } from './created-info';
import UpdatedInfo, { Updated } from './updated-info';

export class Condominium implements Created, Updated {
  @IsString()
  readonly id: string;

  @IsString()
  readonly name: string;

  @IsString()
  readonly address: string;

  @IsString()
  @Transform((params) => params.value && params.value.replace(/\D/g, ''), {
    toPlainOnly: true,
  })
  readonly cnpj?: string;

  @Expose({ name: 'avatar_url' })
  readonly avatarUrl?: string | null;

  @IsString()
  @Expose({ name: 'bond_code' })
  readonly bondCode?: string;

  @Expose({ name: 'full_address' })
  @ValidateNested()
  @Type(() => Address)
  @IsOptional()
  readonly fullAddress?: Address;

  @IsBoolean()
  @Expose({ name: 'is_chat_enabled' })
  readonly isChatEnabled: boolean;

  /** Garante que o serviço seja ativo se não setado */
  @IsBoolean()
  @Expose({ name: 'is_reservations_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isReservationsEnabled: boolean;

  /** Garante que o cadastro seja ativo se não setado */
  @IsBoolean()
  @Expose({ name: 'is_signup_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isSignupEnabled: boolean;

  /** Garante que o alerta de emergencia seja ativo se não setado */
  @IsBoolean()
  @Expose({ name: 'is_emergency_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isEmergencyEnabled: boolean;

  /** Garante que o serviço seja ativo se nao setado  */
  @IsBoolean()
  @Expose({ name: 'is_bikes_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isBikesEnabled: boolean;

  @IsBoolean()
  @Expose({ name: 'is_disabled' })
  @Transform(({ value }) => value ?? false, { toClassOnly: true })
  readonly isDisabled?: boolean;

  @IsBoolean()
  @Expose({ name: 'is_feed_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isFeedEnabled: boolean;

  @IsBoolean()
  @Expose({ name: 'is_guests_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isGuestsEnabled: boolean;

  @IsBoolean()
  @Expose({ name: 'is_help_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isHelpEnabled: boolean;

  @IsBoolean()
  @Expose({ name: 'is_packages_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  readonly isPackagesEnabled: boolean;

  @IsBoolean()
  @Expose({ name: 'is_dweller_post_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @IsOptional()
  readonly isDwellerPostEnabled?: boolean;

  @IsBoolean()
  @Expose({ name: 'is_dweller_comment_enabled' })
  @Transform(({ value }) => value ?? true, { toClassOnly: true })
  @IsOptional()
  readonly isDwellerCommentEnabled?: boolean;

  @IsOptional()
  @IsNumber()
  @Expose({ name: 'reservation_max_period' })
  readonly reservationMaxPeriod?: number;

  @IsString()
  @Expose({ name: 'laundry_id' })
  @IsOptional()
  readonly laundryId?: string;

  @IsString()
  @Expose({ name: 'furniture_id' })
  @IsOptional()
  readonly furnitureId?: string;

  @IsString()
  @Expose({ name: 'cleaning_id' })
  @IsOptional()
  readonly cleaningId?: string;

  @IsString()
  @Expose({ name: 'maid_id' })
  @IsOptional()
  readonly maidId?: string;

  @IsString()
  @Transform((params) => params.value && params.value.replace(/\D/g, ''), {
    toPlainOnly: true,
  })
  readonly phone?: string;

  @IsEnum(CondominiumType)
  readonly type?: CondominiumType;

  @IsString()
  @Expose({ name: 'syndic_name' })
  readonly syndicName?: string;

  @IsString()
  @Expose({ name: 'syndic_phone' })
  @Transform((params) => params.value && params.value.replace(/\D/g, ''), {
    toPlainOnly: true,
  })
  @IsOptional()
  readonly syndicPhone?: string;

  @IsString()
  @Expose({ name: 'caretaker_name' })
  readonly caretakerName?: string;

  @IsString()
  @Expose({ name: 'caretaker_phone' })
  @Transform((params) => params.value && params.value.replace(/\D/g, ''), {
    toPlainOnly: true,
  })
  readonly caretakerPhone?: string;

  @IsString()
  @Expose({ name: 'contact_email' })
  readonly contactEmail?: string;

  @IsOptional()
  @IsObject()
  readonly created?: CreatedInfo;

  @IsOptional()
  @IsObject()
  readonly updated?: UpdatedInfo;

  constructor(init?: Condominium) {
    this.id = init?.id!;
    this.name = init?.name!;
    this.address = init?.address!;
    this.cnpj = init?.cnpj;
    this.bondCode = init?.bondCode;
    this.avatarUrl = init?.avatarUrl;
    this.fullAddress = init?.fullAddress;
    this.isDisabled = init?.isDisabled;
    this.isChatEnabled = init?.isChatEnabled!;
    this.isBikesEnabled = init?.isBikesEnabled!;
    this.isFeedEnabled = init?.isFeedEnabled!;
    this.isGuestsEnabled = init?.isGuestsEnabled!;
    this.isHelpEnabled = init?.isHelpEnabled!;
    this.isReservationsEnabled = init?.isReservationsEnabled!;
    this.isSignupEnabled = init?.isSignupEnabled!;
    this.isEmergencyEnabled = init?.isEmergencyEnabled!;
    this.isPackagesEnabled = init?.isPackagesEnabled!;
    this.isDwellerPostEnabled = init?.isDwellerPostEnabled;
    this.isDwellerCommentEnabled = init?.isDwellerCommentEnabled;
    this.reservationMaxPeriod = init?.reservationMaxPeriod;
    this.phone = init?.phone;
    this.type = init?.type;
    this.syndicName = init?.syndicName;
    this.syndicPhone = init?.syndicPhone;
    this.caretakerName = init?.caretakerName;
    this.caretakerPhone = init?.caretakerPhone;
    this.contactEmail = init?.contactEmail;
    this.laundryId = init?.laundryId!;
    this.furnitureId = init?.furnitureId;
    this.cleaningId = init?.cleaningId;
    this.maidId = init?.maidId;
    this.created = init?.created ? new CreatedInfo(init?.created!) : undefined;
    this.updated = init?.updated ? new UpdatedInfo(init?.updated!) : undefined;
  }
}

export default Condominium;
